import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Box,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { CloseIcon as Close, ContentCopyIcon as ContentCopy, DownloadIcon as Download } from "../../icons";
import { useRootStore } from '../../store/contexts';
import { OverviewTab } from './systemMonitor/OverviewTab';
import { ActivityTab } from './systemMonitor/ActivityTab';
import { PerformanceTab } from './systemMonitor/PerformanceTab';
import { NetworkTab } from './systemMonitor/NetworkTab';
import { StoresTab } from './systemMonitor/StoresTab';
import { BreadcrumbsTab } from './systemMonitor/BreadcrumbsTab';
import { ReferenceCacheTab } from './systemMonitor/ReferenceCacheTab';
import type { TabPanelProps } from './systemMonitor/types';

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-monitor-tabpanel-${index}`}
      aria-labelledby={`system-monitor-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const SystemMonitor: React.FC = observer(() => {
  const rootStore = useRootStore();
  const monitorStore = rootStore.systemMonitorStore;
  const langStore = rootStore.langStore;

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleClose = () => {
    monitorStore.toggleMonitor();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    monitorStore.setSelectedTab(newValue);
  };

  const handleCopyToClipboard = async () => {
    const success = await monitorStore.copySnapshotToClipboard();
    setSnackbar({
      open: true,
      message: success
        ? langStore.monitorSnapshotCopied
        : langStore.monitorCopyFailed,
      severity: success ? 'success' : 'error',
    });
  };

  const handleDownloadSnapshot = async () => {
    await monitorStore.downloadSnapshot();
    setSnackbar({
      open: true,
      message: langStore.monitorSnapshotDownloaded,
      severity: 'success',
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog
        key={`monitor-${langStore.language}`}
        fullScreen
        open={monitorStore.isOpen}
        onClose={handleClose}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6" component="div">
              {langStore.monitorSystemMonitor}
            </Typography>

            <Button
              startIcon={<ContentCopy />}
              onClick={handleCopyToClipboard}
              color="inherit"
              sx={{ mr: 1 }}
            >
              {langStore.monitorCopyJson}
            </Button>

            <Button
              startIcon={<Download />}
              onClick={handleDownloadSnapshot}
              color="inherit"
              sx={{ mr: 2 }}
            >
              {langStore.monitorExportJson}
            </Button>

            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Toolbar>

          <Tabs
            value={monitorStore.selectedTab}
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="secondary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={langStore.monitorOverview} id="system-monitor-tab-0" />
            <Tab label={langStore.monitorActivity} id="system-monitor-tab-1" />
            <Tab label={langStore.monitorPerformance} id="system-monitor-tab-2" />
            <Tab label={langStore.monitorNetwork} id="system-monitor-tab-3" />
            <Tab label={langStore.monitorStores} id="system-monitor-tab-4" />
            <Tab label={langStore.monitorBreadcrumbs} id="system-monitor-tab-5" />
            <Tab label={langStore.monitorCache} id="system-monitor-tab-6" />
          </Tabs>
        </AppBar>

        <TabPanel value={monitorStore.selectedTab} index={0}>
          <OverviewTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={1}>
          <ActivityTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={2}>
          <PerformanceTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={3}>
          <NetworkTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={4}>
          <StoresTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={5}>
          <BreadcrumbsTab />
        </TabPanel>
        <TabPanel value={monitorStore.selectedTab} index={6}>
          <ReferenceCacheTab />
        </TabPanel>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});
